<script>
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore'; // Added orderBy, limit, startAfter
import { useLanguage } from '../composables/useLanguage';
import { onMounted, ref, computed, watch } from 'vue';
import { useHead } from '@vueuse/head';
import { stripHtmlToText } from '../utils/html'; // Import the new helper

export default {
  setup() {
    const posts = ref([]);
    const { lang, supportedLanguages } = useLanguage();

    // Infinite scrolling state
    const lastDoc = ref(null);
    const loading = ref(false);
    const hasMore = ref(true);
    const postsLimit = 5; // Number of posts to fetch per batch
    const statusMessage = ref(''); // For displaying messages like 'loading more...'

    const homePageMeta = computed(() => {
      const meta = {
        en: {
          title: "Top 5 Guru - Your Guide to the Best of Everything",
          description: "Discover the top 5 in various categories, from tech to lifestyle. Your ultimate guide for rankings and reviews.",
          h1: "Latest Articles"
        },
        nl: {
          title: "Top 5 Guru - Jouw Gids voor het Beste van Alles",
          description: "Ontdek de top 5 in diverse categorieën, van technologie tot lifestyle. Jouw ultieme gids voor ranglijsten en recensies.",
          h1: "Nieuwste Artikelen"
        },
        es: {
          title: "Top 5 Guru - Tu Guía para lo Mejor van Alles",
          description: "Descubre el top 5 en varias categorías, desde tecnología hasta estilo de vida. Tu guía definitiva para rankings y reseñas.",
          h1: "Últimos Artículos"
        }
      };
      return meta[lang.value] || meta.en; // Fallback to English
    });

    const currentUrl = computed(() => {
      return `${window.location.origin}/${lang.value}`;
    });

    const generateHreflangLinks = computed(() => {
      const links = [];
      const origin = window.location.origin;

      supportedLanguages.forEach(l => {
        links.push({
          rel: 'alternate',
          hreflang: l,
          href: `${origin}/${l}`
        });
      });

      links.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: `${origin}/en` // x-default points to the English homepage
      });
      return links;
    });

    useHead({
      title: computed(() => homePageMeta.value.title),
      meta: [
        {
          name: 'description',
          content: computed(() => stripHtmlToText(homePageMeta.value.description)),
        },
        { property: 'og:title', content: computed(() => homePageMeta.value.title) },
        { property: 'og:description', content: computed(() => stripHtmlToText(homePageMeta.value.description)) },
        { property: 'og:url', content: currentUrl },
        { property: 'og:site_name', content: 'Top 5 Guru' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: computed(() => homePageMeta.value.title) },
        { name: 'twitter:description', content: computed(() => stripHtmlToText(homePageMeta.value.description)) },
      ],
      link: computed(() => [
        { rel: 'canonical', href: currentUrl.value },
        ...generateHreflangLinks.value
      ])
    });

    const fetchPosts = async (reset = false) => {
      if (loading.value || (!hasMore.value && !reset)) return;

      loading.value = true;
      statusMessage.value = 'Loading articles...';
      
      try {
        console.log(`[DEBUG] Fetching posts for language: ${lang.value}, Reset: ${reset}, LastDoc: ${lastDoc.value ? 'exists' : 'null'}`);
        const articlesRef = collection(db, 'articles');
        // Order by the slug of the current language variant
        let q = query(
                      articlesRef, 
                      where(`languageVariants.${lang.value}.slug`, '>', ''), // Ensure variant exists
                      orderBy('createdAt', 'desc'), // Order by createdAt for newest to oldest
                      orderBy(`languageVariants.${lang.value}.slug`), // Secondary order by slug for stable pagination
                      limit(postsLimit)        );

        if (lastDoc.value && !reset) {
          q = query(q, startAfter(lastDoc.value));
        }
        console.log('[DEBUG] Constructed Firestore query:', q); // Log the query object

        const querySnapshot = await getDocs(q);
        console.log('[DEBUG] Raw query snapshot:', querySnapshot); // Log the raw snapshot
        console.log(`Found ${querySnapshot.docs.length} documents.`);

        if (querySnapshot.empty) {
          hasMore.value = false;
          loading.value = false;
          if (reset) posts.value = []; // Clear if no initial posts
          statusMessage.value = 'No more articles.';
          console.log('No more documents found.');
          return;
        }

        const newPosts = querySnapshot.docs.map(doc => {
          const articleData = doc.data();
          console.log('[DEBUG] Processing document data:', articleData); // Log each document's raw data
          const langVariant = articleData.languageVariants && articleData.languageVariants[lang.value];
          console.log(`[DEBUG] Lang variant for ${lang.value}:`, langVariant); // Log the language variant

          if (!langVariant) {
            console.warn(`[DEBUG] No language variant '${lang.value}' found for document ID: ${doc.id}. Skipping.`);
            return null; // Skip this document if variant is missing
          }
          return {
            slug: langVariant.slug,
            title: langVariant.title,
            shortDescription: langVariant.metaDescription,
            tags: articleData.tags,
          };
        }).filter(Boolean); // Filter out nulls
        console.log('[DEBUG] Mapped new posts:', newPosts); // Log mapped posts


        if (reset) {
          posts.value = newPosts;
        } else {
          posts.value.push(...newPosts);
        }

        lastDoc.value = querySnapshot.docs[querySnapshot.docs.length - 1];
        hasMore.value = querySnapshot.docs.length === postsLimit;
        console.log('✅ Loaded posts:', posts.value.length, 'total. Has more:', hasMore.value);
        statusMessage.value = ''; // Clear status message on success

      } catch (error) {
        console.error('❌ Error fetching posts:', error);
        statusMessage.value = 'Error loading articles.';
      } finally {
        loading.value = false;
      }
    };

    const handleScroll = () => {
      const bottomOfWindow = window.innerHeight + window.scrollY >= document.documentElement.offsetHeight - 100; // 100px buffer
      if (bottomOfWindow && hasMore.value && !loading.value) {
        fetchPosts();
      }
    };

    onMounted(() => {
      fetchPosts(true); // Initial fetch, reset to true
      window.addEventListener('scroll', handleScroll);
    });

    watch(lang, () => {
        posts.value = []; // Clear posts on language change
        lastDoc.value = null; // Reset pagination
        hasMore.value = true; // Assume more posts
        fetchPosts(true); // Refetch posts for new language
    });

    return {
      posts,
      lang,
      homePageMeta,
      loading,
      hasMore,
      statusMessage, // Expose statusMessage
    };
  }
};
</script>

<template>
  <div class="home-container">
        <div v-if="posts.length > 0">      <div v-for="post in posts" :key="post.slug" class="post-card">
        <h2>
          <router-link :to="`/${lang}/article/${post.slug}`">{{ post.title }}</router-link>
        </h2>
        <p v-html="post.shortDescription"></p>
      </div>
      <div class="pagination-status">
        <p v-if="loading">Loading more articles...</p>
        <p v-else-if="!hasMore && posts.length > 0">You've reached the end of the articles.</p>
        <p v-else-if="posts.length === 0 && !loading">{{ statusMessage || 'No articles available.' }}</p>
      </div>
    </div>
    <div v-else>
      <p v-if="loading">{{ statusMessage || 'Loading articles...' }}</p>
      <p v-else-if="!hasMore && posts.length === 0">{{ statusMessage || 'No articles available.' }}</p>
    </div>
  </div>
</template>

<style>
.home-container {
  max-width: 800px;
  margin: auto;
  padding: 20px;
}

/* Home page container */
.home-container h1 {
  text-align: center;
  font-size: 2.5rem;
  color: white;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.4);
  margin-bottom: 30px;
  font-weight: 700;
}

/* Article card styling for HomePage */
.post-card {
  background: rgba(255, 255, 255, 0.92);
  color: #222;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  margin-bottom: 25px;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border: none;
}

.post-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.35);
}

.post-card h2 {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: #222;
}

.post-card a {
  color: #e91e63;
  text-decoration: none;
  transition: color 0.2s;
}

.post-card a:hover {
  color: #c2185b;
  text-decoration: none;
}

.post-card p {
  font-size: 1.05rem;
  line-height: 1.6;
  margin-bottom: 12px;
  color: #333;
}

/* Tag styling */
.post-card strong {
  color: #e91e63;
}

/* Loading message styling */
.home-container > div > p {
  background: rgba(255, 255, 255, 0.8);
  padding: 15px;
  border-radius: 10px;
  text-align: center;
  font-size: 1.2rem;
  color: #555;
}
</style>